import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Trophy,
  ArrowLeft,
  Users,
  Calendar,
  Plus,
  CheckCircle,
  XCircle,
  Trash2,
  Play,
  Edit,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Tournament, TournamentTeam, TournamentMatch, TournamentStatus } from "@shared/schema";

interface TournamentDetailProps {
  tournamentId: string;
  onBack: () => void;
  onEdit: () => void;
}

const statusLabels: Record<TournamentStatus, string> = {
  draft: "Borrador",
  registration_open: "Inscripciones Abiertas",
  registration_closed: "Inscripciones Cerradas",
  in_progress: "En Progreso",
  completed: "Finalizado",
  cancelled: "Cancelado",
};

const statusColors: Record<TournamentStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  registration_open: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  registration_closed: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  in_progress: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const teamStatusLabels: Record<string, string> = {
  pending: "Pendiente",
  confirmed: "Confirmado",
  withdrawn: "Retirado",
  disqualified: "Descalificado",
};

const teamStatusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  confirmed: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  withdrawn: "bg-muted text-muted-foreground",
  disqualified: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const matchStatusLabels: Record<string, string> = {
  scheduled: "Programado",
  in_progress: "En Juego",
  completed: "Finalizado",
  walkover: "W.O.",
  cancelled: "Cancelado",
};

export function TournamentDetail({ tournamentId, onBack, onEdit }: TournamentDetailProps) {
  const { toast } = useToast();
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showEditMatch, setShowEditMatch] = useState<string | null>(null);
  const [deleteTeamId, setDeleteTeamId] = useState<string | null>(null);
  const [newTeam, setNewTeam] = useState({
    teamName: "",
    player1Name: "",
    player2Name: "",
    contactEmail: "",
    contactPhone: "",
  });
  const [matchScore, setMatchScore] = useState<{
    sets: Array<{ team1: string; team2: string }>;
    winnerId: string;
  }>({
    sets: [{ team1: "", team2: "" }, { team1: "", team2: "" }, { team1: "", team2: "" }],
    winnerId: "",
  });

  const { data, isLoading } = useQuery<{
    tournament: Tournament;
    teams: TournamentTeam[];
    matches: TournamentMatch[];
  }>({
    queryKey: ["/api/club/tournaments", tournamentId],
  });

  const addTeamMutation = useMutation({
    mutationFn: async (teamData: typeof newTeam) => {
      return apiRequest("POST", `/api/club/tournaments/${tournamentId}/teams`, teamData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/club/tournaments", tournamentId] });
      toast({ title: "Equipo añadido", description: "El equipo ha sido registrado correctamente." });
      setShowAddTeam(false);
      setNewTeam({ teamName: "", player1Name: "", player2Name: "", contactEmail: "", contactPhone: "" });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: error?.message || "No se pudo añadir el equipo.", 
        variant: "destructive" 
      });
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: async ({ teamId, status }: { teamId: string; status: string }) => {
      return apiRequest("PATCH", `/api/club/tournaments/${tournamentId}/teams/${teamId}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/club/tournaments", tournamentId] });
      toast({ title: "Equipo actualizado" });
    },
    onError: () => {
      toast({ title: "Error", description: "No se pudo actualizar el equipo.", variant: "destructive" });
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: async (teamId: string) => {
      return apiRequest("DELETE", `/api/club/tournaments/${tournamentId}/teams/${teamId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/club/tournaments", tournamentId] });
      toast({ title: "Equipo eliminado" });
      setDeleteTeamId(null);
    },
    onError: () => {
      toast({ title: "Error", description: "No se pudo eliminar el equipo.", variant: "destructive" });
    },
  });

  const generateBracketMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/club/tournaments/${tournamentId}/generate-bracket`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/club/tournaments", tournamentId] });
      toast({ title: "Cuadro generado", description: "Los partidos han sido creados correctamente." });
    },
    onError: (error: any) => {
      toast({ 
        title: "Error", 
        description: error?.message || "No se pudo generar el cuadro.", 
        variant: "destructive" 
      });
    },
  });

  const updateMatchMutation = useMutation({
    mutationFn: async ({ matchId, data }: { matchId: string; data: any }) => {
      return apiRequest("PATCH", `/api/club/tournaments/${tournamentId}/matches/${matchId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/club/tournaments", tournamentId] });
      toast({ title: "Partido actualizado" });
      setShowEditMatch(null);
    },
    onError: () => {
      toast({ title: "Error", description: "No se pudo actualizar el partido.", variant: "destructive" });
    },
  });

  const formatDate = (date: Date | string | null) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getTeamName = (teamId: string | null) => {
    if (!teamId || !data?.teams) return "Por determinar";
    const team = data.teams.find(t => t.id === teamId);
    return team?.teamName || "Por determinar";
  };

  const handleSaveMatchResult = () => {
    if (!showEditMatch || !matchScore.winnerId) {
      toast({ title: "Error", description: "Selecciona un ganador.", variant: "destructive" });
      return;
    }

    const validSets = matchScore.sets
      .filter(s => s.team1 !== "" && s.team2 !== "")
      .map(s => ({ team1: parseInt(s.team1), team2: parseInt(s.team2) }));

    if (validSets.length === 0) {
      toast({ title: "Error", description: "Ingresa al menos un set.", variant: "destructive" });
      return;
    }

    updateMatchMutation.mutate({
      matchId: showEditMatch,
      data: {
        score: { sets: validSets },
        winnerId: matchScore.winnerId,
        status: "completed",
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!data?.tournament) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Torneo no encontrado</p>
        <Button onClick={onBack} className="mt-4">Volver</Button>
      </div>
    );
  }

  const { tournament, teams, matches } = data;
  const confirmedTeams = teams.filter(t => t.status === "confirmed" || t.status === "pending");
  const canGenerateBracket = 
    (tournament.status === "registration_closed" || tournament.status === "draft") && 
    confirmedTeams.length >= (tournament.minTeams || 4) &&
    matches.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="button-back">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-bold">{tournament.name}</h2>
            <Badge className={statusColors[tournament.status as TournamentStatus]}>
              {statusLabels[tournament.status as TournamentStatus]}
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm mt-1 flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {formatDate(tournament.startDate)}
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {teams.length} / {tournament.maxTeams} equipos
            </span>
          </p>
        </div>
        <Button variant="outline" onClick={onEdit} data-testid="button-edit-tournament">
          <Edit className="h-4 w-4 mr-2" />
          Editar
        </Button>
      </div>

      <Tabs defaultValue="teams">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="teams" data-testid="tab-teams">
            <Users className="h-4 w-4 mr-2" />
            Equipos ({teams.length})
          </TabsTrigger>
          <TabsTrigger value="matches" data-testid="tab-matches">
            <Trophy className="h-4 w-4 mr-2" />
            Partidos ({matches.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="teams" className="space-y-4 mt-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-muted-foreground">
              {confirmedTeams.length} equipos confirmados de {tournament.minTeams || 4} mínimo necesarios
            </p>
            {(tournament.status === "draft" || tournament.status === "registration_open") && (
              <Button onClick={() => setShowAddTeam(true)} data-testid="button-add-team">
                <Plus className="h-4 w-4 mr-2" />
                Añadir Equipo
              </Button>
            )}
          </div>

          {teams.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-semibold mb-2">No hay equipos inscritos</h3>
                <p className="text-muted-foreground mb-6">
                  Añade equipos al torneo para comenzar
                </p>
                {(tournament.status === "draft" || tournament.status === "registration_open") && (
                  <Button onClick={() => setShowAddTeam(true)} data-testid="button-add-first-team">
                    <Plus className="h-4 w-4 mr-2" />
                    Añadir Primer Equipo
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {teams.map((team, index) => (
                <Card key={team.id} data-testid={`card-team-${team.id}`}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                          {team.seedNumber || index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{team.teamName}</p>
                          <p className="text-sm text-muted-foreground">
                            {team.player1Name || "Jugador 1"} / {team.player2Name || "Jugador 2"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={teamStatusColors[team.status || "pending"]}>
                          {teamStatusLabels[team.status || "pending"]}
                        </Badge>
                        {team.status === "pending" && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => updateTeamMutation.mutate({ teamId: team.id, status: "confirmed" })}
                            data-testid={`button-confirm-team-${team.id}`}
                          >
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          </Button>
                        )}
                        {team.status === "confirmed" && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => updateTeamMutation.mutate({ teamId: team.id, status: "withdrawn" })}
                            data-testid={`button-withdraw-team-${team.id}`}
                          >
                            <XCircle className="h-4 w-4 text-yellow-600" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setDeleteTeamId(team.id)}
                          data-testid={`button-delete-team-${team.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="matches" className="space-y-4 mt-4">
          {canGenerateBracket && (
            <Card className="border-primary/50 bg-primary/5">
              <CardContent className="py-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-medium">Generar Cuadro del Torneo</p>
                    <p className="text-sm text-muted-foreground">
                      Tienes {confirmedTeams.length} equipos listos. Se generarán los partidos automáticamente.
                    </p>
                  </div>
                  <Button 
                    onClick={() => generateBracketMutation.mutate()}
                    disabled={generateBracketMutation.isPending}
                    data-testid="button-generate-bracket"
                  >
                    {generateBracketMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Generar Cuadro
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {matches.length === 0 && !canGenerateBracket ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Trophy className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-semibold mb-2">No hay partidos programados</h3>
                <p className="text-muted-foreground">
                  {confirmedTeams.length < (tournament.minTeams || 4) 
                    ? `Necesitas al menos ${tournament.minTeams || 4} equipos para generar el cuadro`
                    : "Genera el cuadro para crear los partidos"
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {Array.from(new Set(matches.map(m => m.round))).sort((a, b) => a - b).map(round => {
                const roundMatches = matches.filter(m => m.round === round);
                const roundName = round === Math.max(...matches.map(m => m.round)) 
                  ? "Final" 
                  : round === Math.max(...matches.map(m => m.round)) - 1 
                    ? "Semifinales" 
                    : `Ronda ${round}`;

                return (
                  <Card key={round}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{roundName}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {roundMatches.map(match => (
                        <div 
                          key={match.id} 
                          className="p-3 rounded-lg bg-muted/50 flex items-center justify-between gap-4"
                          data-testid={`match-${match.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 text-sm">
                              <span className={match.winnerId === match.team1Id ? "font-bold text-green-600" : ""}>
                                {getTeamName(match.team1Id)}
                              </span>
                              <span className="text-muted-foreground">vs</span>
                              <span className={match.winnerId === match.team2Id ? "font-bold text-green-600" : ""}>
                                {getTeamName(match.team2Id)}
                              </span>
                            </div>
                            {match.score && (
                              <p className="text-sm text-muted-foreground mt-1">
                                {(match.score as any).sets?.map((s: any, i: number) => 
                                  `${s.team1}-${s.team2}`
                                ).join(", ")}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {matchStatusLabels[match.status || "scheduled"]}
                            </Badge>
                            {match.status !== "completed" && match.team1Id && match.team2Id && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setMatchScore({
                                    sets: [{ team1: "", team2: "" }, { team1: "", team2: "" }, { team1: "", team2: "" }],
                                    winnerId: "",
                                  });
                                  setShowEditMatch(match.id);
                                }}
                                data-testid={`button-edit-match-${match.id}`}
                              >
                                Registrar Resultado
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={showAddTeam} onOpenChange={setShowAddTeam}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Añadir Equipo</DialogTitle>
            <DialogDescription>
              Registra un nuevo equipo para el torneo
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre del Equipo *</Label>
              <Input
                value={newTeam.teamName}
                onChange={(e) => setNewTeam({ ...newTeam, teamName: e.target.value })}
                placeholder="Ej: Los Invencibles"
                data-testid="input-team-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Jugador 1 *</Label>
                <Input
                  value={newTeam.player1Name}
                  onChange={(e) => setNewTeam({ ...newTeam, player1Name: e.target.value })}
                  placeholder="Nombre completo"
                  data-testid="input-player1-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Jugador 2 *</Label>
                <Input
                  value={newTeam.player2Name}
                  onChange={(e) => setNewTeam({ ...newTeam, player2Name: e.target.value })}
                  placeholder="Nombre completo"
                  data-testid="input-player2-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email de Contacto</Label>
                <Input
                  type="email"
                  value={newTeam.contactEmail}
                  onChange={(e) => setNewTeam({ ...newTeam, contactEmail: e.target.value })}
                  placeholder="email@ejemplo.com"
                  data-testid="input-team-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={newTeam.contactPhone}
                  onChange={(e) => setNewTeam({ ...newTeam, contactPhone: e.target.value })}
                  placeholder="+1 809 000 0000"
                  data-testid="input-team-phone"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTeam(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={() => addTeamMutation.mutate(newTeam)}
              disabled={!newTeam.teamName || !newTeam.player1Name || !newTeam.player2Name || addTeamMutation.isPending}
              data-testid="button-save-team"
            >
              {addTeamMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Añadir Equipo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showEditMatch} onOpenChange={() => setShowEditMatch(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Resultado</DialogTitle>
            <DialogDescription>
              Ingresa el resultado del partido
            </DialogDescription>
          </DialogHeader>
          {showEditMatch && (() => {
            const match = matches.find(m => m.id === showEditMatch);
            if (!match) return null;
            return (
              <div className="space-y-4 py-4">
                <div className="text-center mb-4">
                  <p className="font-medium">
                    {getTeamName(match.team1Id)} vs {getTeamName(match.team2Id)}
                  </p>
                </div>
                
                <div className="space-y-3">
                  {matchScore.sets.map((set, index) => (
                    <div key={index} className="grid grid-cols-5 gap-2 items-center">
                      <span className="text-sm text-muted-foreground">Set {index + 1}</span>
                      <Input
                        type="number"
                        min={0}
                        max={7}
                        value={set.team1}
                        onChange={(e) => {
                          const newSets = [...matchScore.sets];
                          newSets[index] = { ...newSets[index], team1: e.target.value };
                          setMatchScore({ ...matchScore, sets: newSets });
                        }}
                        placeholder="0"
                        className="text-center"
                        data-testid={`input-set${index + 1}-team1`}
                      />
                      <span className="text-center">-</span>
                      <Input
                        type="number"
                        min={0}
                        max={7}
                        value={set.team2}
                        onChange={(e) => {
                          const newSets = [...matchScore.sets];
                          newSets[index] = { ...newSets[index], team2: e.target.value };
                          setMatchScore({ ...matchScore, sets: newSets });
                        }}
                        placeholder="0"
                        className="text-center"
                        data-testid={`input-set${index + 1}-team2`}
                      />
                      <div></div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label>Ganador *</Label>
                  <Select
                   
                    value={matchScore.winnerId}
                    onValueChange={(v) => setMatchScore({ ...matchScore, winnerId: v })}
                  >
                    <SelectTrigger data-testid="select-winner">
                      <SelectValue placeholder="Selecciona el ganador" />
                    </SelectTrigger>
                    <SelectContent>
                      {match.team1Id && (
                        <SelectItem value={match.team1Id}>{getTeamName(match.team1Id)}</SelectItem>
                      )}
                      {match.team2Id && (
                        <SelectItem value={match.team2Id}>{getTeamName(match.team2Id)}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditMatch(null)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveMatchResult}
              disabled={updateMatchMutation.isPending}
              data-testid="button-save-match-result"
            >
              {updateMatchMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar Resultado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTeamId} onOpenChange={() => setDeleteTeamId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar equipo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTeamId && deleteTeamMutation.mutate(deleteTeamId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
